import { GROW_SHARE_ROUTES, type WidthToken } from '@backrs_ui/common-ui';

import { type PaymentPageProps } from './pages/Subscription/SubscriptionPageContainer';
import { type CustomTabId } from './redux/tabs/customTabIds';
import {
  type PaywallPermissionList,
  type RestrictedPermission,
} from './redux/user/user.transformer';

// Route paths and route-config types. This module must stay free of runtime
// component imports (type-only is fine) so importing a route constant never
// pulls the page tree — and an App.tsx import cycle — into the module graph.

export type LegacyPageTypes = 'UNAUTHENTICATED' | 'BACKRS_4.0' | 'LUBAV_4.0';

export type PageBackground =
  | 'default' // Beige/25
  | 'beige' // Beige/50
  | 'brand' // Brand/25 — light blue
  | 'brand-dots' // Brand/25 + radial dot pattern
  | 'brand-grid' // Brand/25 + crosshatch grid
  | 'white' // Pure white
  | 'white-grid'; // White + crosshatch grid

export const APP_ROUTE = {
  // COMMON
  LOGIN: '/login',
  OTP: '/login/verify',
  SIGN_UP: '/signup',
  VERIFY_EMAIL: '/verify-email',
  RESET_PASSWORD: '/reset-password',
  AUTH_CALLBACK: '/auth/callback',
  CONNECT_APP_AUTHORIZE: '/connect/authorize',

  // GROW
  HOME: '/',
  UNIVERSITY: GROW_SHARE_ROUTES.UNIVERSITY,
  EMPLOYER: GROW_SHARE_ROUTES.EMPLOYER,
  INTERNSHIP: GROW_SHARE_ROUTES.INTERNSHIP,
  SEARCH: '/search',
  LIFE_READINESS: '/life-readiness',
  LIFE_READINESS_QUIZ: '/life-readiness/:categoryId/:subcategoryId',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  SHARED_SESSION_GRANTS: '/shared-session-grants',
  SUBSCRIPTION: '/subscription',
  SUBSCRIPTION_RETURN: '/subscription/return',
  PRIORITIES: '/priorities',
  PLAYBOOK: '/playbooks/:playbookId',
  PLAYBOOK_TASK: '/playbooks/tasks/:taskId',
  EDUCATIONAL_INVESTMENT: '/educational-investment',
  EDUCATIONAL_INVESTMENT_WITH_CODE: '/educational-investment/:code',
  ASSESS_MY_TRACK: '/are-you-on-track',
  ASSESS_MY_TRACK_RESULT: '/are-you-on-track/:trackAssessmentResultUuid',
  TOOLS: '/tools',
  SAT_ACT_BOOSTER: '/sat-act-booster',
  SAT_ACT_BOOSTER_GENERATING: '/sat-act-booster/generating',
  SAT_ACT_BOOSTER_RESULT: '/sat-act-booster/result',

  // BLUEPRINT
  CAREER_PATHS: '/career-paths',
  CAREER_TRACKS: '/career-tracks',
  MINIMUM_TRACK: '/minimum-track',
  SURVEY: '/survey',
  GENERATING_BLUEPRINT: '/generating-blueprint',
  MY_BLUEPRINT: '/my-blueprint',
  MARKERS_OF_SUCCESS: '/markers-of-success',

  // DEMO BLUEPRINT (for CEO demo)
  DEMO_SURVEY: '/demo/survey',
  DEMO_GENERATING_BLUEPRINT: '/demo/generating-blueprint',
  DEMO_MY_BLUEPRINT: '/demo/my-blueprint',

  // SHARED BLUEPRINT
  SHARED_CAREER_PATHS: '/shared/:uuid/career-paths',
  SHARED_MINIMUM_TRACK: '/shared/:uuid/minimum-track',
  SHARED_MY_BLUEPRINT: '/shared/:uuid/my-blueprint',
  SHARED_EDUCATIONAL_INVESTMENT: '/shared/:uuid/educational-investment',

  // SHARED SOLUTION SESSION
  SHARED_SOLUTION_SESSION: '/shared/:uuid/solution-session/:sessionType',

  // SHARED OFFICE HOURS SESSION
  SHARED_OFFICE_HOURS_SESSION: '/shared/:uuid/office-hours/:sessionType',

  // PROSPER
  LIBRARY: '/four-unlocks-library',
  COMPETENCY: GROW_SHARE_ROUTES.COMPETENCY,
  BOOKMARKS: '/bookmarks',
  MY_COLLEGES: '/my-college-list',
  COMPARE_COLLEGES: '/my-college-list/compare',

  // DEEP DIVES
  DEEP_DIVES: '/deep-dives',
  DEEP_DIVE_DETAIL: '/deep-dives/:deepDiveId',

  // COACHES
  COACHES: '/coaches',

  // LUBAV
  REGISTER: '/register',
  COACHING_DASHBOARD: '/coaching-dashboard',
  COACHING_DASHBOARD_MEMBER: '/coaching-dashboard/:memberId',
  COACHING_ANALYTICS: '/coaching-analytics',
  ROSTERS_ADMIN: '/cohorts',
  OFFICE_HOURS_SUPERUSER: '/superuser',
  OFFICE_HOURS_SESSION: '/office-hours/:id',
  OFFICE_HOURS: '/office-hours',
  SOLUTION_SESSION: '/solution-session/:id?',

  // PROTOTYPE TOOLS
  INDUSTRIES: '/industries',
  SUCCESS_SYSTEM: '/success-system',
  KEY_LEVERS: '/key-levers',

  // GPA GUIDE
  GPA_GUIDE: '/gpa-guide',

  // COMMON APP ESSAY GUIDE
  COMMON_APP_ESSAY_GUIDE: '/common-app-essay-guide',

  // COURSE SELECTION GUIDE
  COURSE_SELECTION_GUIDE: '/course-selection-guide',

  // COLLEGE APPLICATION ROADMAP GUIDE
  COLLEGE_APPLICATION_ROADMAP_GUIDE: '/college-application-roadmap',

  // STUDY HABITS GUIDE
  STUDY_HABITS_GUIDE: '/study-habits-guide',

  // STANDARDIZED TESTS GUIDE
  STANDARDIZED_TESTS_GUIDE: '/standardized-tests-guide',

  // EXTRACURRICULARS GUIDE
  EXTRACURRICULARS_GUIDE: '/extracurriculars-guide',

  // INTERNSHIP GUIDE
  INTERNSHIPS_GUIDE: '/internships-guide',

  // ONLINE REPUTATION GUIDE
  ONLINE_REPUTATION_GUIDE: '/online-reputation-guide',

  // COLLEGE VISIT GUIDE
  COLLEGE_VISIT_GUIDE: '/college-visit-guide',

  // MAJORS GUIDE
  MAJORS: '/majors',
  MAJORS_DETAIL: '/majors/:majorId',
  MAJORS_COLLEGE_GUIDE: '/majors/college-guide',
  MAJORS_GRAD_SCHOOL_GUIDE: '/majors/grad-school-guide',
  MAJORS_SEARCH: '/search/majors',
  MAJORS_EXPLORER: '/majors/explorer',

  // NUGGETS
  NUGGETS: '/nuggets',

  // ALL OTHERS (404)
  NOT_FOUND: '*',
  NOT_FOUND_404: '/404',
  COMING_SOON: '/coming-soon',
} as const;

/** Pages coaches work from. Add new coaching pages here so the shell treats them as one. */
export const COACHING_ROUTE_PATHS: readonly string[] = [
  APP_ROUTE.COACHING_DASHBOARD,
  APP_ROUTE.COACHING_DASHBOARD_MEMBER,
  APP_ROUTE.COACHING_ANALYTICS,
];

/** A route-config value that can be static or derived from route params + pathname. */
export type RouteValue<T> =
  | T
  | ((params: Record<string, string | undefined>, pathname: string) => T);

export const resolveRouteValue = <T>(
  value: RouteValue<T> | undefined,
  params: Record<string, string | undefined>,
  pathname: string,
): T | undefined =>
  typeof value === 'function'
    ? (value as (p: typeof params, s: string) => T)(params, pathname)
    : value;

export type LubavPanelSize = 'small' | 'large' | 'hidden';

export type ShellMode = Readonly<{
  layout?: 'page' | 'resourceTabsOnly';
  lubavPanel: RouteValue<LubavPanelSize>;
  staticTabs?: readonly CustomTabId[];
}>;

export const DEFAULT_SHELL_MODE: ShellMode = { lubavPanel: 'small' };

export type AppRoute = Readonly<{
  protected: boolean;
  path: (typeof APP_ROUTE)[keyof typeof APP_ROUTE];
  page: JSX.Element;
  queryParam?: string[];
  legacyPageType?: LegacyPageTypes;
  /** Background preset applied by PageShell. Defaults to 'default' (Beige/25). Accepts a function for dynamic backgrounds. */
  background?: RouteValue<PageBackground>;
  subRoute?: string;
  requiredPermissions?: PaywallPermissionList;
  paymentPageProps?: PaymentPageProps;
  title: RouteValue<string>;
  /** Permissions the user must have, or they're redirected home. Distinct from `requiredPermissions`, which shows the paywall instead. */
  redirectIfMissingPermissions?: RestrictedPermission[];
  shellMode?: ShellMode;
  /** Max content width for this page. Accepts a design token (e.g. 'width-default', 'width-3xl') or a raw CSS value. */
  maxWidth?: WidthToken | (string & {});
}>;

/** The react-router path pattern for a route, including any query-param segments. */
export const routePathPattern = (route: AppRoute): string =>
  route.queryParam?.length ? `${route.path}/:${route.queryParam.join('/:')}` : route.path;
